export interface PublicSkillTheme {
  key: string;
  name: string;
  description: string;
  sortOrder: number;
  active: boolean;
}

export interface PublicSkillProfile {
  id: string;
  handle: string;
  displayName: string;
  bio: string;
  imageUrl: string | null;
  websiteUrl: string | null;
  status: "unclaimed" | "claimed" | "suspended";
}

export interface PublicSkill {
  id: string;
  path: string;
  slug: string;
  displayName: string;
  summary: string;
  author: {
    id: string;
    handle: string;
    displayName: string;
    bio: string;
    imageUrl: string | null;
    websiteUrl: string | null;
    claimed: boolean;
  };
  theme: { key: string; name: string } | null;
  tags: string[];
  capability: string;
  compatibility: string[];
  version: { id: string; number: number };
  downloads: number;
  rating: { count: number; average: number | null };
  featured: boolean;
  publishedAt: string;
  updatedAt: string;
}

export interface PublicSkillCatalog {
  skills: PublicSkill[];
  themes: PublicSkillTheme[];
}

export interface PublicSkillAdminSnapshot {
  profiles: Array<PublicSkillProfile & { accountConnected: boolean }>;
  skills: Array<{
    skill_id: string;
    profile_id: string;
    slug: string;
    display_name: string;
    summary: string;
    theme_key: string | null;
    status: "draft" | "published" | "hidden";
    is_featured: number;
    author_handle: string;
    download_count: number;
    rating_count: number;
    rating_sum: number;
    updated_at: string;
    tags_json: string;
  }>;
  claims: Array<{
    claim_id: string;
    profile_id: string;
    handle: string;
    status: "pending" | "claimed" | "revoked" | "expired";
    expires_at: string;
    created_at: string;
  }>;
  themes: PublicSkillTheme[];
}

export interface OwnedPublicSkill {
  skill_id: string;
  slug: string;
  display_name: string;
  summary: string;
  theme_key: string | null;
  status: "draft" | "published" | "hidden";
  is_featured: number;
  updated_at: string;
  version_number: number;
  download_count: number;
  rating_count: number;
  rating_sum: number;
  tags_json: string;
  source_type?: "legacy" | "upload" | "team" | "github" | "seed" | "remix";
}

export interface PublicTeamSkillSource {
  workspaceId: string;
  skillId: string;
  versionId: string;
  versionNumber: number;
}

export interface PublicSkillProvenance {
  sourceType: "legacy" | "upload" | "doorway" | "team" | "github" | "seed" | "remix";
  repositoryUrl?: string | null;
  repositoryPath?: string | null;
  requestedRef?: string | null;
  resolvedSha?: string | null;
  workspaceId?: string | null;
  teamSkillId?: string | null;
  teamVersionId?: string | null;
  parentSkillId?: string | null;
  parentVersionId?: string | null;
  parentPath?: string | null;
  parentDisplayName?: string | null;
}

export interface PublicSkillVersionSummary {
  id: string;
  number: number;
  label: "Current" | "Previous" | "Pending" | "Restored" | string;
  baseVersionId: string | null;
  releaseNotes: string | null;
  archiveSha256: string;
  archiveBytes: number;
  fileCount: number;
  manifestDigest: string;
  capabilityDigest: string;
  createdAt: string;
  provenance: PublicSkillProvenance;
}

export interface PublicSkillVersionDetail extends PublicSkillVersionSummary {
  manifest: Record<string, unknown>;
  capability: {
    classification?: string;
    scripts?: string[];
    externalTools?: string[];
    dependencies?: string[];
    outboundDestinations?: string[];
    [key: string]: unknown;
  };
  files: Array<{
    path: string;
    size: number;
    sha256: string;
    mode: string;
    detectedType: string;
  }>;
  checks: {
    validator: string;
    validationResult: string;
    checkedAt: string;
    explanation?: string;
  };
}

export interface PublicSkillFilePreview {
  path: string;
  size: number;
  sha256: string;
  mode: string;
  detectedType: string;
  text: string | null;
  truncated: boolean;
  truncationReason: string | null;
}

export interface PublicSkillComparison {
  base: { id: string; number: number };
  target: { id: string | null; number: number };
  summary: string;
  files: Array<{
    path: string;
    status: "added" | "changed" | "removed" | "unchanged";
    base?: Record<string, unknown>;
    target?: Record<string, unknown>;
    textDiff?: {
      lines?: Array<{ type: "added" | "removed" | "context"; text: string }>;
      truncated?: boolean;
      truncationReason?: string | null;
    };
  }>;
  capabilities: {
    fromClassification?: unknown;
    toClassification?: unknown;
    changed: boolean;
    added: Record<string, string[]>;
    removed: Record<string, string[]>;
  };
}

export interface PublicSkillInstallDescriptor {
  skillId: string;
  versionId: string;
  versionNumber: number;
  archiveSha256: string;
  downloadUrl: string;
  compatibility: string[];
  requirements: string[];
  clients: Array<{
    id: "claude" | "codex" | "zip";
    label: string;
    instruction: string;
    command?: string;
  }>;
}

export interface PublicSkillInspection {
  id: string;
  expiresAt?: string;
  source: {
    type: "upload" | "team" | "github";
    repositoryUrl?: string;
    repositoryPath?: string | null;
    requestedRef?: string | null;
    resolvedSha?: string | null;
  };
  proposed: {
    slug: string;
    displayName: string;
    summary: string;
    compatibility: string[];
    tags: string[];
    themeKey?: string | null;
  };
  manifest: Record<string, unknown>;
  capability: Record<string, unknown>;
  files: Array<{ path: string; size: number; detectedType?: string }>;
  checks: Array<{ key: string; label: string; result: "passed" | "review_required"; detail?: string }>;
}

export interface PublicSkillGitHubUpdate {
  available: boolean;
  current: {
    versionId: string;
    versionNumber: number;
    resolvedSha: string;
    archiveSha256: string;
  };
  latest: {
    resolvedSha: string;
    archiveSha256: string;
    source: PublicSkillInspection["source"];
    proposed: PublicSkillInspection["proposed"];
    checks: PublicSkillInspection["checks"];
  };
  comparison?: PublicSkillComparison;
}

export async function loadPublicSkillCatalog(
  filters: { query?: string; theme?: string; tag?: string; sort?: string } = {},
): Promise<PublicSkillCatalog> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) if (value) query.set(key, value);
  return request(`/api/library/v1/catalog${query.size ? `?${query}` : ""}`);
}

export async function loadPublicSkillProfile(handle: string): Promise<{
  profile: PublicSkillProfile;
  skills: PublicSkill[];
  canonicalPath: string;
}> {
  return request(`/api/library/v1/profiles/${encodeURIComponent(handle)}`);
}

export async function loadPublicSkill(handle: string, slug: string): Promise<{
  skill: PublicSkill;
  canonicalPath: string;
}> {
  return request(`/api/library/v1/profiles/${encodeURIComponent(handle)}/skills/${encodeURIComponent(slug)}`);
}

export async function loadPublicSkillVersions(skillId: string): Promise<{
  skill: { id: string; currentVersionId: string };
  versions: PublicSkillVersionSummary[];
}> {
  const versions: PublicSkillVersionSummary[] = [];
  let before: number | null = null;
  let skill: { id: string; currentVersionId: string } | null = null;
  do {
    const query: string = before === null ? "" : `?before=${before}`;
    const page: {
      skill: { id: string; currentVersionId: string };
      versions: PublicSkillVersionSummary[];
      nextBefore: number | null;
    } = await request(`/api/library/v1/skills/${encodeURIComponent(skillId)}/versions${query}`);
    skill = page.skill;
    versions.push(...page.versions);
    before = page.nextBefore;
  } while (before !== null);
  return { skill: skill!, versions };
}

export async function loadPublicSkillVersion(skillId: string, versionId: string): Promise<{
  version: PublicSkillVersionDetail;
}> {
  return request(
    `/api/library/v1/skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}`,
  );
}

export async function loadPublicSkillPreview(
  skillId: string,
  versionId: string,
  path = "SKILL.md",
): Promise<{ file: PublicSkillFilePreview }> {
  const query = new URLSearchParams({ path });
  return request(
    `/api/library/v1/skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}/preview?${query}`,
  );
}

export async function loadPublicSkillComparison(
  skillId: string,
  versionId: string,
  baseVersionId?: string,
): Promise<{ comparison: PublicSkillComparison }> {
  const query = new URLSearchParams();
  if (baseVersionId) query.set("baseVersionId", baseVersionId);
  return request(
    `/api/library/v1/skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}/compare${query.size ? `?${query}` : ""}`,
  );
}

export async function loadPublicSkillInstall(
  skillId: string,
  versionId: string,
): Promise<{ install: PublicSkillInstallDescriptor }> {
  return request(
    `/api/library/v1/skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}/install`,
  );
}

export async function loadMyPublicSkillProfile(): Promise<PublicSkillProfile | null> {
  const body = await request<{ profile: PublicSkillProfile | null }>("/api/library/v1/me/profile");
  return body.profile;
}

export async function saveMyPublicSkillProfile(input: {
  handle: string;
  displayName: string;
  bio?: string;
  imageUrl?: string;
  websiteUrl?: string;
}): Promise<PublicSkillProfile> {
  const body = await request<{ profile: PublicSkillProfile }>("/api/library/v1/me/profile", {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return body.profile;
}

export async function publishPublicSkill(input: {
  slug: string;
  displayName: string;
  summary: string;
  themeKey?: string;
  tags: string[];
  files: File[];
  teamSkillSource?: PublicTeamSkillSource;
  inspectionId?: string;
  remixSource?: { skillId: string; versionId: string };
}): Promise<{
  id: string;
  path: string;
  version: number;
  versionId?: string;
  archiveSha256?: string;
  auditEventId?: string;
}> {
  const source = input.teamSkillSource
    ? {
        workspaceId: input.teamSkillSource.workspaceId,
        skillId: input.teamSkillSource.skillId,
        versionId: input.teamSkillSource.versionId,
      }
    : undefined;
  const body = await request<{ skill: { id: string; path: string; version: number } }>("/api/library/v1/me/skills", {
    method: "POST",
    body: JSON.stringify({
      slug: input.slug,
      displayName: input.displayName,
      summary: input.summary,
      themeKey: input.themeKey || null,
      tags: input.tags,
      ...(input.remixSource ? { remixSource: input.remixSource } : {}),
      ...(input.inspectionId
        ? { inspectionId: input.inspectionId }
        : source
          ? { teamSkillSource: source }
          : { files: await browserFiles(input.files) }),
    }),
  });
  return body.skill;
}

export async function inspectPublicSkill(input: {
  files?: File[];
  teamSkillSource?: PublicTeamSkillSource;
  githubSource?: { repositoryUrl: string; ref?: string; path?: string };
}): Promise<PublicSkillInspection> {
  const body = await request<{ inspection: PublicSkillInspection }>("/api/library/v1/me/skills/inspect", {
    method: "POST",
    body: JSON.stringify({
      ...(input.files?.length ? { files: await browserFiles(input.files) } : {}),
      ...(input.teamSkillSource ? {
        teamSkillSource: {
          workspaceId: input.teamSkillSource.workspaceId,
          skillId: input.teamSkillSource.skillId,
          versionId: input.teamSkillSource.versionId,
        },
      } : {}),
      ...(input.githubSource ? { githubSource: input.githubSource } : {}),
    }),
  });
  return body.inspection;
}

export async function loadMyPublicSkills(): Promise<OwnedPublicSkill[]> {
  const body = await request<{ skills: OwnedPublicSkill[] }>("/api/library/v1/me/skills");
  return body.skills;
}

export async function updateMyPublicSkill(skillId: string, input: {
  displayName?: string;
  summary?: string;
  themeKey?: string | null;
  tags?: string[];
  status?: "draft" | "published" | "hidden";
}): Promise<void> {
  await request(`/api/library/v1/me/skills/${encodeURIComponent(skillId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function publishPublicSkillVersion(skillId: string, files: File[]): Promise<{
  id: string;
  path: string;
  version: number;
}> {
  const body = await request<{ skill: { id: string; path: string; version: number } }>(
    `/api/library/v1/me/skills/${encodeURIComponent(skillId)}/versions`,
    {
      method: "POST",
      body: JSON.stringify({ files: await browserFiles(files) }),
    },
  );
  return body.skill;
}

export async function checkPublicSkillGitHubUpdate(skillId: string): Promise<PublicSkillGitHubUpdate> {
  const body = await request<{ update: PublicSkillGitHubUpdate }>(
    `/api/library/v1/me/skills/${encodeURIComponent(skillId)}/github-update`,
  );
  return body.update;
}

export async function applyPublicSkillGitHubUpdate(
  skillId: string,
  expectedResolvedSha: string,
  expectedCurrentVersionId: string,
  expectedCurrentArchiveSha256: string,
  releaseNotes?: string,
): Promise<{ applied?: boolean; reason?: string; versionId?: string }> {
  const body = await request<{ update?: { applied?: boolean; reason?: string; versionId?: string } }>(
    `/api/library/v1/me/skills/${encodeURIComponent(skillId)}/github-update`,
    {
      method: "POST",
      body: JSON.stringify({
        expectedResolvedSha,
        expectedCurrentVersionId,
        expectedCurrentArchiveSha256,
        ...(releaseNotes ? { releaseNotes } : {}),
      }),
    },
  );
  return body.update ?? {};
}

export async function ratePublicSkill(skillId: string, rating: number): Promise<void> {
  await request(`/api/library/v1/skills/${encodeURIComponent(skillId)}/rating`, {
    method: "PUT",
    body: JSON.stringify({ rating }),
  });
}

export async function acceptPublicSkillProfileClaim(token: string): Promise<PublicSkillProfile> {
  const body = await request<{ profile: PublicSkillProfile }>(
    `/api/library/v1/claims/${encodeURIComponent(token)}/accept`,
    { method: "POST" },
  );
  return body.profile;
}

export async function loadPublicSkillAdmin(): Promise<PublicSkillAdminSnapshot> {
  return request("/api/admin/v1/library");
}

export async function createGuestPublicSkillProfile(input: {
  handle: string;
  displayName: string;
  bio?: string;
}): Promise<PublicSkillProfile> {
  const body = await request<{ profile: PublicSkillProfile }>("/api/admin/v1/library/profiles", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return body.profile;
}

export async function createPublicSkillClaim(profileId: string): Promise<{
  id: string;
  token: string;
  url: string;
  expiresAt: string;
}> {
  const body = await request<{ claim: { id: string; token: string; url: string; expiresAt: string } }>(
    `/api/admin/v1/library/profiles/${encodeURIComponent(profileId)}/claims`,
    { method: "POST", body: JSON.stringify({ expiresInDays: 7 }) },
  );
  return body.claim;
}

export async function updateAdminPublicSkillProfile(profileId: string, input: {
  handle: string;
  displayName: string;
  bio?: string;
  imageUrl?: string | null;
  websiteUrl?: string | null;
  status?: "unclaimed" | "claimed" | "suspended";
}): Promise<void> {
  await request(`/api/admin/v1/library/profiles/${encodeURIComponent(profileId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function createPublicSkillTheme(input: {
  key: string;
  displayName: string;
  description?: string;
  sortOrder?: number;
}): Promise<void> {
  await request("/api/admin/v1/library/themes", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updatePublicSkillTheme(themeKey: string, input: {
  displayName: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
}): Promise<void> {
  await request(`/api/admin/v1/library/themes/${encodeURIComponent(themeKey)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function updateAdminPublicSkill(skillId: string, input: {
  displayName?: string;
  summary?: string;
  themeKey?: string | null;
  tags?: string[];
  status?: "draft" | "published" | "hidden";
  isFeatured?: boolean;
}): Promise<void> {
  await request(`/api/admin/v1/library/skills/${encodeURIComponent(skillId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function publishAdminPublicSkill(input: {
  profileId: string;
  slug: string;
  displayName: string;
  summary: string;
  themeKey?: string;
  tags: string[];
  files: File[];
}): Promise<{ id: string; path: string; version: number }> {
  const body = await request<{ skill: { id: string; path: string; version: number } }>(
    `/api/admin/v1/library/profiles/${encodeURIComponent(input.profileId)}/skills`,
    {
      method: "POST",
      body: JSON.stringify({
        slug: input.slug,
        displayName: input.displayName,
        summary: input.summary,
        themeKey: input.themeKey || null,
        tags: input.tags,
        files: await browserFiles(input.files),
      }),
    },
  );
  return body.skill;
}

export async function publishAdminPublicSkillVersion(skillId: string, files: File[]): Promise<{
  id: string;
  path: string;
  version: number;
}> {
  const body = await request<{ skill: { id: string; path: string; version: number } }>(
    `/api/admin/v1/library/skills/${encodeURIComponent(skillId)}/versions`,
    {
      method: "POST",
      body: JSON.stringify({ files: await browserFiles(files) }),
    },
  );
  return body.skill;
}

async function browserFiles(files: File[]) {
  const paths = files.map((file) => file.webkitRelativePath || file.name);
  const firstParts = new Set(paths.map((path) => path.split("/")[0]));
  const removeRoot = firstParts.size === 1 && paths.every((path) => path.includes("/"));
  return Promise.all(files.map(async (file, index) => ({
    path: removeRoot ? paths[index]!.split("/").slice(1).join("/") : paths[index]!,
    mode: "0644",
    bytesBase64: bytesToBase64(new Uint8Array(await file.arrayBuffer())),
  })));
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary);
}

async function request<T = Record<string, unknown>>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      accept: "application/json",
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  });
  const body = await response.json().catch(() => ({})) as T & { message?: string };
  if (!response.ok) throw new Error(body.message ?? "Doorway could not complete the Skills Library request.");
  return body;
}
